"use client";

import {
    useEffect,
    useRef,
} from "react";

const VISITOR_ARRIVAL_KEY =
    "visitorArrival";

const COOKIE_CONSENT_KEY =
    "cookieConsent";

function getSearchParamValue(
    searchParams,
    key
) {
    return (
        searchParams.get(
            key
        ) || null
    );
}

function getCookieValue(
    name
) {
    try {
        const cookiePrefix =
            `${name}=`;

        const cookieParts =
            document.cookie.split(
                ";"
            );

        for (
            const cookiePart
            of cookieParts
        ) {
            const trimmedCookie =
                cookiePart.trim();

            if (
                trimmedCookie.startsWith(
                    cookiePrefix
                )
            ) {
                return decodeURIComponent(
                    trimmedCookie.substring(
                        cookiePrefix.length
                    )
                );
            }
        }

        return null;
    } catch (error) {
        return null;
    }
}

function createFbcFromFbclid(
    fbclid,
    arrivedAtMs
) {
    if (!fbclid) {
        return null;
    }

    return `fb.1.${arrivedAtMs}.${fbclid}`;
}

function getHungarianDateTime() {
    return new Date().toLocaleString(
        "hu-HU",
        {
            timeZone:
                "Europe/Budapest",
            year:
                "numeric",
            month:
                "2-digit",
            day:
                "2-digit",
            hour:
                "2-digit",
            minute:
                "2-digit",
            second:
                "2-digit",
        }
    );
}

function getSavedConsent() {
    try {
        const savedConsent =
            window.sessionStorage.getItem(
                COOKIE_CONSENT_KEY
            );

        if (!savedConsent) {
            return null;
        }

        const parsedConsent =
            JSON.parse(
                savedConsent
            );

        if (
            !parsedConsent ||
            typeof parsedConsent !==
                "object"
        ) {
            return null;
        }

        return parsedConsent;
    } catch (error) {
        return null;
    }
}

function createSecurityArrival() {
    return {
        userAgent:
            window.navigator
                .userAgent ||
            null,
        arrivedAt:
            getHungarianDateTime(),
        referrer:
            null,
        utm: {
            source:
                null,
            medium:
                null,
            campaign:
                null,
            id:
                null,
            term:
                null,
            content:
                null,
        },
    };
}

function createConsentedArrival(
    securityArrival,
    attribution,
    consent
) {
    const analyticsAllowed =
        consent?.analytics ===
        true;

    const marketingAllowed =
        consent?.marketing ===
        true;

    const fbclid =
        marketingAllowed
            ? attribution.marketing
                  .fbclid
            : null;

    const savedFbc =
        marketingAllowed
            ? getCookieValue(
                "_fbc"
            )
            : null;

    const savedFbp =
        marketingAllowed
            ? getCookieValue(
                "_fbp"
            )
            : null;

    return {
        ...securityArrival,

        referrer:
            analyticsAllowed
                ? attribution.referrer
                : null,

        utm:
            analyticsAllowed
                ? {
                      ...attribution.utm,
                  }
                : {
                      source:
                          null,
                      medium:
                          null,
                      campaign:
                          null,
                      id:
                          null,
                      term:
                          null,
                      content:
                          null,
                  },

        attribution:
            marketingAllowed
                ? {
                      fbclid,

                      fbc:
                          savedFbc ||
                          createFbcFromFbclid(
                              fbclid,
                              attribution.arrivedAtMs
                          ),

                      fbp:
                          savedFbp,

                      gclid:
                          attribution
                              .marketing
                              .gclid,

                      gbraid:
                          attribution
                              .marketing
                              .gbraid,

                      wbraid:
                          attribution
                              .marketing
                              .wbraid,

                      ttclid:
                          attribution
                              .marketing
                              .ttclid,
                  }
                : {
                      fbclid:
                          null,
                      fbc:
                          null,
                      fbp:
                          null,
                      gclid:
                          null,
                      gbraid:
                          null,
                      wbraid:
                          null,
                      ttclid:
                          null,
                  },
    };
}

function saveVisitorArrival(
    visitorArrival
) {
    try {
        window.sessionStorage.setItem(
            VISITOR_ARRIVAL_KEY,
            JSON.stringify(
                visitorArrival
            )
        );

        return true;
    } catch (error) {
        console.log(
            "Visitor arrival could not be saved."
        );

        return false;
    }
}

export default function VisitorAttributionTracker() {
    const securityArrivalRef =
        useRef(null);

    const attributionRef =
        useRef(null);

    useEffect(() => {
        const searchParams =
            new URLSearchParams(
                window.location.search
            );

        const securityArrival =
            createSecurityArrival();

        securityArrivalRef.current =
            securityArrival;

        attributionRef.current = {
            arrivedAtMs:
                Date.now(),

            referrer:
                document.referrer ||
                null,

            utm: {
                source:
                    getSearchParamValue(
                        searchParams,
                        "utm_source"
                    ),
                medium:
                    getSearchParamValue(
                        searchParams,
                        "utm_medium"
                    ),
                campaign:
                    getSearchParamValue(
                        searchParams,
                        "utm_campaign"
                    ),
                id:
                    getSearchParamValue(
                        searchParams,
                        "utm_id"
                    ),
                term:
                    getSearchParamValue(
                        searchParams,
                        "utm_term"
                    ),
                content:
                    getSearchParamValue(
                        searchParams,
                        "utm_content"
                    ),
            },

            marketing: {
                fbclid:
                    getSearchParamValue(
                        searchParams,
                        "fbclid"
                    ),

                gclid:
                    getSearchParamValue(
                        searchParams,
                        "gclid"
                    ),

                gbraid:
                    getSearchParamValue(
                        searchParams,
                        "gbraid"
                    ),

                wbraid:
                    getSearchParamValue(
                        searchParams,
                        "wbraid"
                    ),

                ttclid:
                    getSearchParamValue(
                        searchParams,
                        "ttclid"
                    ),
            },
        };

        const savedConsent =
            getSavedConsent();

        if (
            savedConsent
                ?.analytics ===
                true ||
            savedConsent
                ?.marketing ===
                true
        ) {
            const consentedArrival =
                createConsentedArrival(
                    securityArrival,
                    attributionRef.current,
                    savedConsent
                );

            saveVisitorArrival(
                consentedArrival
            );

            return;
        }

        saveVisitorArrival(
            securityArrival
        );

        const handleConsentSaved =
            (event) => {
                const nextConsent =
                    event.detail
                        ?.consent ||
                    null;

                const attribution =
                    attributionRef.current;

                const securityArrival =
                    securityArrivalRef.current;

                if (
                    !attribution ||
                    !securityArrival
                ) {
                    return;
                }

                if (
                    nextConsent
                        ?.analytics !==
                        true &&
                    nextConsent
                        ?.marketing !==
                        true
                ) {
                    saveVisitorArrival(
                        securityArrival
                    );

                    return;
                }

                const consentedArrival =
                    createConsentedArrival(
                        securityArrival,
                        attribution,
                        nextConsent
                    );

                saveVisitorArrival(
                    consentedArrival
                );
            };

        window.addEventListener(
            "cookie-consent-saved",
            handleConsentSaved
        );

        return () => {
            window.removeEventListener(
                "cookie-consent-saved",
                handleConsentSaved
            );

            securityArrivalRef.current =
                null;

            attributionRef.current =
                null;
        };
    }, []);

    return null;
}